import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { tokens, Tooltip } from '../design-system';
import { ArrowDownTrayIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useExportIdea, useIdeaPlans } from '../hooks/api/useIdeas';
import { useSubscription } from '../contexts/SubscriptionContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ── Catalog definition ────────────────────────────────────────────────────────

const CATALOG = [
  // Stakeholder
  {
    id: 'markdown',
    name: 'Product Brief',
    format: '.md',
    category: 'Stakeholder',
    description: 'A human-readable document covering your idea overview, product specification, features, and plans. Great for sharing with stakeholders or dropping into your docs.',
    thumbnail: 'product-brief',
  },
  {
    id: 'one_pager',
    name: 'One-Pager',
    format: '.md',
    category: 'Stakeholder',
    description: 'Elevator pitch format: problem, solution, who it\'s for, top features, and success metrics on a single page. The thing you send to a VP who won\'t read a full brief.',
    thumbnail: 'one-pager',
  },
  {
    id: 'pitch_deck',
    name: 'Pitch Deck Outline',
    format: '.md',
    category: 'Stakeholder',
    description: 'Slide-by-slide blueprint (title, problem, solution, market, product, roadmap, metrics) with your spec content pre-filled. Use as the foundation for your deck.',
    thumbnail: 'slides',
  },
  {
    id: 'statement_of_work',
    name: 'Statement of Work',
    format: '.md',
    category: 'Stakeholder',
    description: "What will be built, what won't, deliverables with acceptance criteria, constraints, and assumptions. For handing off scope to a team or client.",
    thumbnail: 'statement-of-work',
  },

  // Engineering
  {
    id: 'architecture_overview',
    name: 'Architecture Overview',
    format: '.md',
    category: 'Engineering',
    description: 'System-level architecture document (arc42 style): context & scope, solution strategy, building block view, runtime behaviour (commands/events/read models), deployment topology, crosscutting concerns, and architecture decision records.',
    thumbnail: 'arch-overview',
  },
  {
    id: 'technical_spec',
    name: 'Technical Spec',
    format: '.md',
    category: 'Engineering',
    description: 'Feature-scoped engineering spec with event model tables (commands, events, read models), user stories with acceptance criteria checklists, and functional requirements. Pick a feature to generate a focused spec.',
    thumbnail: 'technical-spec',
    interactive: true,
  },
  {
    id: 'sprint_brief',
    name: 'Sprint Brief',
    format: '.md',
    category: 'Engineering',
    description: 'User stories and acceptance criteria formatted for a sprint, grouped by feature. Ready to paste into Jira, Linear, or any sprint planning tool.',
    thumbnail: 'sprint-brief',
    interactive: true,
    interactiveType: 'plan',
  },
  {
    id: 'feature_prd',
    name: 'Feature PRD',
    format: '.md',
    category: 'Engineering',
    description: 'A focused Product Requirements Document for a single feature — overview, user stories, acceptance criteria, event model, and success metrics. Great for handing off a specific feature to engineering.',
    thumbnail: 'feature-prd',
    interactive: true,
  },
  {
    id: 'test_plan',
    name: 'Test Plan',
    format: '.md',
    category: 'Engineering',
    description: 'Acceptance criteria from every user story reformatted as numbered test cases, grouped by feature. Ready to hand to QA or paste into a test management tool.',
    thumbnail: 'test-plan',
    interactive: true,
    interactiveType: 'plan',
  },

  // Planning
  {
    id: 'rtm',
    name: 'Requirements Traceability Matrix',
    format: '.md',
    category: 'Planning',
    description: 'Grid linking every feature to its user stories and acceptance criteria, with stable Req IDs for linking to test cases.',
    thumbnail: 'rtm',
  },
  {
    id: 'wbs',
    name: 'Work Breakdown Structure',
    format: '.md',
    category: 'Planning',
    description: 'Feature → story → acceptance criteria as a numbered outline. The decomposition Cahoots generates, exported in the format PMs and project managers expect.',
    thumbnail: 'wbs',
  },
  {
    id: 'risk_register',
    name: 'Risk Register',
    format: '.md',
    category: 'Planning',
    description: 'All identified risks with severity, category, mitigation, and unique IDs. Sorted high → medium → low. Ready to track in any project management tool.',
    thumbnail: 'risk-register',
  },
  {
    id: 'story_map',
    name: 'Story Map',
    format: '.md',
    category: 'Planning',
    description: 'Features as columns, user stories beneath each one. The classic story mapping layout in a shareable document — great for sprint planning and stakeholder walkthroughs.',
    thumbnail: 'story-map',
  },

  // AI Tools
  {
    id: 'claude_md',
    name: 'CLAUDE.md',
    format: '.md',
    category: 'AI Tools',
    description: 'A ready-to-drop-in CLAUDE.md for Claude Code. Includes project context, tech stack, event modeling rules, commands, events, and read models — everything a coding agent needs.',
    thumbnail: 'code',
    codeVariant: 'claude',
  },
  {
    id: 'cursor_rules',
    name: 'Cursor Rules',
    format: '.txt',
    category: 'AI Tools',
    description: 'A .cursorrules file with your project context, tech stack, and event modeling conventions. Drop it in the root of your repo for Cursor to pick up automatically.',
    thumbnail: 'code',
    codeVariant: 'gray',
  },
  {
    id: 'readme',
    name: 'README',
    format: '.md',
    category: 'AI Tools',
    description: 'A standard README.md with project overview, feature list, prerequisites from your tech stack, getting started steps, architecture notes, and contributing guidelines.',
    thumbnail: 'readme',
  },
  {
    id: 'ai_starter_kit',
    name: 'AI Starter Kit',
    format: '.zip',
    category: 'AI Tools',
    description: 'A ZIP with a domain model, per-feature story files, and an implementation guide — everything an AI coding agent needs to start building. Pick a plan to export. Starting a new project from scratch? This kit includes a full project scaffold.',
    thumbnail: 'code',
    codeVariant: 'zip',
    interactive: true,
    interactiveType: 'plan',
    isZip: true,
  },
  {
    id: 'ai_update_kit',
    name: 'AI Update Kit',
    format: '.zip',
    category: 'AI Tools',
    description: "A drop-in .cahoots/ folder for projects already in development. Contains your domain model and feature stories formatted for AI coding agents — no scaffold, no boilerplate. Replace your existing .cahoots/ or drop it in fresh. Requires a plan.",
    thumbnail: 'code',
    codeVariant: 'update-kit',
    interactive: true,
    interactiveType: 'plan',
    isZip: true,
  },

  // Data & Publishing
  {
    id: 'json',
    name: 'Raw Data',
    format: '.json',
    category: 'Data',
    description: 'Complete structured export of your idea including all plans, user stories, event models, and configuration. Designed for AI agents, automation, or custom tooling.',
    thumbnail: 'json',
  },
];

const MIME_TYPES = {
  json: 'application/json',
  backlog_csv: 'text/csv',
  cursor_rules: 'text/plain',
  ai_starter_kit: 'application/zip',
  ai_update_kit: 'application/zip',
};
const getMimeType = (id) => MIME_TYPES[id] || 'text/markdown';

// ── Thumbnails ────────────────────────────────────────────────────────────────

const PAPER = {
  backgroundColor: '#FFFBEC',
  borderRadius: '3px',
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(46,33,9,0.12), 0 1px 4px rgba(46,33,9,0.06)',
  transform: 'rotate(-1.5deg)',
};

const BG_LIGHT = '#FDF7E5';

function Line({ w = 100, h = 3, color = '#c8c8c8', mb = 4 }) {
  return <div style={{ height: h, background: color, borderRadius: 2, width: `${w}%`, marginBottom: mb }} />;
}

// Product Brief — structured doc with sections and bullets
function ProductBriefThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 150 }}>
        <div style={{ height: 4, backgroundColor: '#e5a83a' }} />
        <div style={{ padding: '12px 12px 18px' }}>
          <Line w={75} h={7} color="#1a1a1a" mb={5} />
          <Line w={50} h={4} color="#999" mb={10} />
          <div style={{ height: 1, background: '#ddd', marginBottom: 10 }} />
          <Line w={100} mb={5} /><Line w={88} mb={5} /><Line w={94} mb={10} />
          <Line w={45} h={5} color="#555" mb={8} />
          {[85, 70, 78].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
              <div style={{ width: 3, height: 3, borderRadius: '50%', background: '#999', flexShrink: 0 }} />
              <div style={{ height: 3, background: '#ccc', borderRadius: 2, width: `${w}%` }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// One-Pager — minimal, big title, 3 metric stat boxes
function OnePagerThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 155, transform: 'rotate(-1deg)' }}>
        <div style={{ height: 4, backgroundColor: '#f97316' }} />
        <div style={{ padding: '14px 12px 14px' }}>
          <Line w={82} h={8} color="#111" mb={4} />
          <Line w={55} h={4} color="#aaa" mb={14} />
          <div style={{ height: 1, background: '#eee', marginBottom: 12 }} />
          <Line w={100} h={3} mb={5} /><Line w={88} h={3} mb={5} /><Line w={72} h={3} mb={16} />
          {/* Metric boxes */}
          <div style={{ display: 'flex', gap: 5 }}>
            {['#fff7ed', '#fff7ed', '#fff7ed'].map((bg, i) => (
              <div key={i} style={{ flex: 1, background: '#fef3e2', border: '1px solid #fde68a', borderRadius: 3, padding: '5px 4px' }}>
                <div style={{ height: 6, background: '#f97316', borderRadius: 2, width: '70%', marginBottom: 3 }} />
                <div style={{ height: 3, background: '#fed7aa', borderRadius: 2, width: '90%' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Architecture Overview — boxes connected by arrows (C4 style system diagram)
function ArchOverviewThumbnail() {
  const boxes = [
    { label: 'API', color: '#6366f1', x: 55, y: 18 },
    { label: 'DB', color: '#0ea5e9', x: 20, y: 56 },
    { label: 'Events', color: '#22c55e', x: 90, y: 56 },
    { label: 'Queue', color: '#f59e0b', x: 55, y: 88 },
  ];
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 160, transform: 'rotate(-1deg)' }}>
        <div style={{ height: 4, backgroundColor: '#6366f1' }} />
        <div style={{ padding: '10px 10px 14px' }}>
          <Line w={65} h={5} color="#111" mb={10} />
          {/* Diagram area */}
          <div style={{ position: 'relative', height: 90, marginBottom: 8 }}>
            {/* Connection lines */}
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
              <line x1="50%" y1="28" x2="22%" y2="58" stroke="#6366f1" strokeWidth="1" strokeDasharray="3 2" opacity="0.5" />
              <line x1="50%" y1="28" x2="78%" y2="58" stroke="#6366f1" strokeWidth="1" strokeDasharray="3 2" opacity="0.5" />
              <line x1="22%" y1="68" x2="46%" y2="85" stroke="#0ea5e9" strokeWidth="1" strokeDasharray="3 2" opacity="0.4" />
              <line x1="78%" y1="68" x2="54%" y2="85" stroke="#22c55e" strokeWidth="1" strokeDasharray="3 2" opacity="0.4" />
            </svg>
            {boxes.map((box) => (
              <div key={box.label} style={{
                position: 'absolute',
                left: `${box.x - 14}%`,
                top: `${box.y - 10}%`,
                width: 28,
                background: box.color + '22',
                border: `1px solid ${box.color}88`,
                borderRadius: 3,
                padding: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{ height: 4, width: '70%', background: box.color, borderRadius: 1, opacity: 0.9 }} />
              </div>
            ))}
          </div>
          <Line w={80} h={3} mb={4} /><Line w={65} h={3} mb={0} />
        </div>
      </div>
    </div>
  );
}

// Technical Spec — shows a markdown table (commands/events)
function TechnicalSpecThumbnail() {
  const rows = [
    { name: 'CreateIdea', triggers: 'IdeaCreated' },
    { name: 'GenerateSpec', triggers: 'SpecGenerated' },
    { name: 'CreatePlan', triggers: 'PlanCreated' },
  ];
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 170, transform: 'rotate(-1deg)' }}>
        <div style={{ height: 4, backgroundColor: '#3b82f6' }} />
        <div style={{ padding: '10px 8px 12px' }}>
          <Line w={70} h={6} color="#111" mb={8} />
          {/* Table */}
          <div style={{ border: '1px solid #e5e5e5', borderRadius: 2, overflow: 'hidden', fontSize: 0 }}>
            {/* Header */}
            <div style={{ display: 'flex', background: '#f0f4ff', borderBottom: '1px solid #dde5f5' }}>
              <div style={{ flex: 2, padding: '3px 5px', borderRight: '1px solid #dde5f5' }}>
                <div style={{ height: 4, background: '#3b82f6', borderRadius: 1, width: '80%' }} />
              </div>
              <div style={{ flex: 3, padding: '3px 5px' }}>
                <div style={{ height: 4, background: '#3b82f6', borderRadius: 1, width: '70%' }} />
              </div>
            </div>
            {rows.map((row, i) => (
              <div key={i} style={{ display: 'flex', borderBottom: i < rows.length - 1 ? '1px solid #eee' : 'none', background: i % 2 ? '#fafafa' : '#fff' }}>
                <div style={{ flex: 2, padding: '3px 5px', borderRight: '1px solid #eee' }}>
                  <div style={{ height: 3, background: '#555', borderRadius: 1, width: '90%' }} />
                </div>
                <div style={{ flex: 3, padding: '3px 5px' }}>
                  <div style={{ height: 3, background: '#22c55e', borderRadius: 1, width: '75%', opacity: 0.7 }} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8 }}>
            <Line w={55} h={5} color="#444" mb={6} />
            <Line w={100} mb={4} /><Line w={85} mb={4} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Sprint Brief — story cards with checkboxes
function SprintBriefThumbnail() {
  const stories = [
    { w: 88, priority: '#8b5cf6' },
    { w: 70, priority: '#3b82f6' },
    { w: 80, priority: '#8b5cf6' },
  ];
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 155, transform: 'rotate(-1deg)' }}>
        <div style={{ height: 4, backgroundColor: '#8b5cf6' }} />
        <div style={{ padding: '10px 10px 12px' }}>
          {/* Feature label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
            <div style={{ height: 4, background: '#8b5cf6', borderRadius: 2, width: '40%', opacity: 0.6 }} />
          </div>
          {stories.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 9, padding: '5px 6px', background: '#f9f8ff', border: '1px solid #ede9fe', borderRadius: 3 }}>
              {/* Checkbox */}
              <div style={{ width: 8, height: 8, border: '1.5px solid #a78bfa', borderRadius: 1.5, flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <div style={{ height: 4, background: '#333', borderRadius: 1, width: `${s.w}%`, marginBottom: 4 }} />
                <div style={{ height: 3, background: s.priority, borderRadius: 2, width: '35%', opacity: 0.6 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Feature PRD — focused single-feature doc with stories and a callout box
function FeaturePRDThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 160, transform: 'rotate(-1deg)' }}>
        <div style={{ height: 4, backgroundColor: '#6366f1' }} />
        <div style={{ padding: '10px 10px 12px' }}>
          {/* Feature badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '2px 7px', marginBottom: 8 }}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#6366f1' }} />
            <div style={{ height: 4, background: '#6366f1', borderRadius: 2, width: 40, opacity: 0.7 }} />
          </div>
          {/* Title */}
          <Line w={85} h={6} color="#111" mb={3} />
          <Line w={100} h={3} mb={4} /><Line w={78} h={3} mb={10} />
          {/* Stories header */}
          <Line w={45} h={4} color="#555" mb={6} />
          {/* 2 story items with checkbox */}
          {[82, 70].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6, padding: '4px 5px', background: '#f5f5ff', border: '1px solid #e0e7ff', borderRadius: 2 }}>
              <div style={{ width: 7, height: 7, border: '1.5px solid #818cf8', borderRadius: 1.5, flexShrink: 0 }} />
              <div style={{ height: 3, background: '#444', borderRadius: 1, width: `${w}%` }} />
            </div>
          ))}
          {/* AC checkboxes */}
          <div style={{ marginTop: 6, paddingLeft: 12 }}>
            {[65, 75].map((w, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                <div style={{ width: 5, height: 5, border: '1px solid #a5b4fc', borderRadius: 1, flexShrink: 0 }} />
                <div style={{ height: 2.5, background: '#bbb', borderRadius: 1, width: `${w}%` }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// README — prose + embedded code block
function ReadmeThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 155, transform: 'rotate(-1.5deg)' }}>
        <div style={{ height: 4, backgroundColor: '#22c55e' }} />
        <div style={{ padding: '10px 10px 14px' }}>
          <Line w={60} h={8} color="#111" mb={6} />
          <Line w={100} mb={4} /><Line w={85} mb={10} />
          {/* Code block */}
          <div style={{ background: '#EEE2BF', borderRadius: 3, padding: '6px 8px', marginBottom: 8, border: '1px solid #D7C9A1' }}>
            <div style={{ height: 3, background: '#6B7A3E', borderRadius: 1, width: '70%', marginBottom: 4 }} />
            <div style={{ height: 3, background: '#EA7C1F', borderRadius: 1, width: '55%', marginBottom: 4 }} />
            <div style={{ height: 3, background: '#A39068', borderRadius: 1, width: '80%' }} />
          </div>
          <Line w={50} h={5} color="#444" mb={6} />
          <Line w={100} mb={4} /><Line w={70} mb={0} />
        </div>
      </div>
    </div>
  );
}

// Release Notes — changelog with version badge and bullet list
function ReleaseNotesThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 155, transform: 'rotate(-1deg)' }}>
        <div style={{ height: 4, backgroundColor: '#14b8a6' }} />
        <div style={{ padding: '10px 10px 14px' }}>
          {/* Version badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <div style={{ height: 6, background: '#111', borderRadius: 1, width: '30%' }} />
            <div style={{ padding: '2px 6px', background: '#ccfbf1', border: '1px solid #99f6e4', borderRadius: 8 }}>
              <div style={{ height: 3, background: '#14b8a6', borderRadius: 1, width: 24 }} />
            </div>
          </div>
          <Line w={55} h={4} color="#666" mb={7} />
          {[78, 65, 82].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
              <div style={{ width: 3, height: 3, borderRadius: '50%', background: '#14b8a6', flexShrink: 0, opacity: 0.8 }} />
              <div style={{ height: 3, background: '#bbb', borderRadius: 1, width: `${w}%` }} />
            </div>
          ))}
          <div style={{ height: 1, background: '#e5e5e5', margin: '8px 0' }} />
          {/* Second version - partial */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{ height: 5, background: '#333', borderRadius: 1, width: '28%' }} />
            <div style={{ padding: '2px 5px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 8 }}>
              <div style={{ height: 3, background: '#999', borderRadius: 1, width: 20 }} />
            </div>
          </div>
          <Line w={70} mb={4} /><Line w={55} mb={0} />
        </div>
      </div>
    </div>
  );
}

function SlidesThumbnail() {
  const slides = [
    { rotate: '4deg', translate: '8px, -4px', zIndex: 1, opacity: 0.5 },
    { rotate: '1.5deg', translate: '4px, -2px', zIndex: 2, opacity: 0.75 },
    { rotate: '-1deg', translate: '0px, 0px', zIndex: 3, opacity: 1 },
  ];
  return (
    <div style={{
      width: '100%', height: '100%',
      background: BG_LIGHT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '28px',
    }}>
      <div style={{ position: 'relative', width: '150px', height: '110px' }}>
        {slides.map((s, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            backgroundColor: '#f8f7f4',
            borderRadius: '4px',
            transform: `rotate(${s.rotate}) translate(${s.translate})`,
            zIndex: s.zIndex,
            opacity: s.opacity,
            boxShadow: '0 4px 12px rgba(46,33,9,0.12)',
            overflow: 'hidden',
          }}>
            <div style={{ height: '5px', background: '#a855f7' }} />
            <div style={{ padding: '10px 10px 8px' }}>
              <div style={{ height: '6px', background: '#222', borderRadius: '2px', width: '65%', marginBottom: '6px' }} />
              {[90, 75, 82].map((w, j) => (
                <div key={j} style={{ height: '3px', background: '#ccc', borderRadius: '2px', width: `${w}%`, marginBottom: '4px' }} />
              ))}
              <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                {[40, 35, 40].map((w, j) => (
                  <div key={j} style={{ height: '18px', background: '#e8e4f0', borderRadius: '3px', flex: `0 0 ${w}%` }} />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TableThumbnail() {
  const rows = [
    { cols: ['#1', 'Feature A', 'Story title...', '5 pts'], header: false },
    { cols: ['#2', 'Feature A', 'Another story', '3 pts'], header: false },
    { cols: ['#3', 'Feature B', 'Third story',   '8 pts'], header: false },
    { cols: ['#4', 'Feature B', 'And one more',  '2 pts'], header: false },
  ];
  return (
    <div style={{
      width: '100%', height: '100%',
      background: BG_LIGHT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '185px',
        borderRadius: '5px',
        overflow: 'hidden',
        border: '1px solid #D7C9A1',
        boxShadow: '0 4px 12px rgba(46,33,9,0.10)',
      }}>
        {/* Header row */}
        <div style={{ display: 'flex', backgroundColor: '#EEE2BF', borderBottom: '1px solid #D7C9A1' }}>
          {['#', 'Feature', 'Title', 'Pts'].map((h, i) => (
            <div key={i} style={{
              flex: i === 2 ? 2 : 1,
              padding: '5px 6px',
              fontSize: '7px', fontFamily: 'monospace',
              color: '#6E5A38', fontWeight: 'bold',
              borderRight: i < 3 ? '1px solid #D7C9A1' : 'none',
            }}>
              {h}
            </div>
          ))}
        </div>
        {/* Data rows */}
        {rows.map((row, i) => (
          <div key={i} style={{
            display: 'flex',
            backgroundColor: i % 2 === 0 ? '#FFFBEC' : '#FDF7E5',
            borderBottom: i < rows.length - 1 ? '1px solid #EEE2BF' : 'none',
          }}>
            {row.cols.map((cell, j) => (
              <div key={j} style={{
                flex: j === 2 ? 2 : 1,
                padding: '5px 6px',
                fontSize: '7px', fontFamily: 'monospace',
                color: j === 0 ? '#A39068' : '#4A3510',
                borderRight: j < 3 ? '1px solid #EEE2BF' : 'none',
                overflow: 'hidden', whiteSpace: 'nowrap',
              }}>
                {cell}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const CODE_VARIANTS = {
  default: {
    bg: 'linear-gradient(135deg, #21252b 0%, #1a1d23 100%)',
    editorBg: '#282c34',
    lines: [
      { indent: 0, parts: [{ t: '{', c: '#e5c07b' }] },
      { indent: 1, parts: [{ t: '"idea"', c: '#61afef' }, { t: ': {', c: '#e5c07b' }] },
      { indent: 2, parts: [{ t: '"name"', c: '#61afef' }, { t: ': ', c: '#abb2bf' }, { t: '"My App"', c: '#98c379' }] },
      { indent: 2, parts: [{ t: '"spec"', c: '#61afef' }, { t: ': {…}', c: '#abb2bf' }] },
      { indent: 1, parts: [{ t: '},', c: '#e5c07b' }] },
      { indent: 1, parts: [{ t: '"plans"', c: '#61afef' }, { t: ': [', c: '#e5c07b' }] },
      { indent: 2, parts: [{ t: '{…}', c: '#abb2bf' }] },
      { indent: 1, parts: [{ t: ']', c: '#e5c07b' }] },
      { indent: 0, parts: [{ t: '}', c: '#e5c07b' }] },
    ],
  },
  blue: {
    bg: 'linear-gradient(135deg, #0f1724 0%, #0a1020 100%)',
    editorBg: '#131d2e',
    lines: [
      { indent: 0, parts: [{ t: '### POST /register-user', c: '#61afef' }] },
      { indent: 0, parts: [{ t: 'Command:', c: '#abb2bf' }, { t: ' RegisterUser', c: '#e5c07b' }] },
      { indent: 0, parts: [{ t: 'Emits:', c: '#abb2bf' }, { t: ' UserRegistered', c: '#98c379' }] },
      { indent: 0, parts: [{ t: '', c: '#abb2bf' }] },
      { indent: 0, parts: [{ t: '| Field  | Type   |', c: '#4b6680' }] },
      { indent: 0, parts: [{ t: '| email  | string |', c: '#abb2bf' }] },
      { indent: 0, parts: [{ t: '| name   | string |', c: '#abb2bf' }] },
    ],
  },
  claude: {
    bg: 'linear-gradient(135deg, #1f1208 0%, #170d04 100%)',
    editorBg: '#261505',
    lines: [
      { indent: 0, parts: [{ t: '# CLAUDE.md', c: '#e5a83a' }] },
      { indent: 0, parts: [{ t: '', c: '' }] },
      { indent: 0, parts: [{ t: '## Project Overview', c: '#e5c07b' }] },
      { indent: 0, parts: [{ t: 'AI planning tool...', c: '#888' }] },
      { indent: 0, parts: [{ t: '', c: '' }] },
      { indent: 0, parts: [{ t: '## Commands', c: '#e5c07b' }] },
      { indent: 0, parts: [{ t: '- `CreateIdea`', c: '#98c379' }] },
      { indent: 0, parts: [{ t: '- `GenerateSpec`', c: '#98c379' }] },
    ],
  },
  gray: {
    bg: 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)',
    editorBg: '#1e1e1e',
    lines: [
      { indent: 0, parts: [{ t: 'You are working on', c: '#888' }] },
      { indent: 0, parts: [{ t: '**MyApp**.', c: '#ccc' }] },
      { indent: 0, parts: [{ t: '', c: '' }] },
      { indent: 0, parts: [{ t: '## Rules', c: '#aaa' }] },
      { indent: 0, parts: [{ t: '- Commands: imperative', c: '#777' }] },
      { indent: 0, parts: [{ t: '- Events: past tense', c: '#777' }] },
      { indent: 0, parts: [{ t: '- Each cmd → 1+ event', c: '#777' }] },
    ],
  },
  zip: {
    bg: 'linear-gradient(135deg, #0d1f0f 0%, #071209 100%)',
    editorBg: '#111c12',
    lines: [
      { indent: 0, parts: [{ t: 'CLAUDE.md', c: '#e5a83a' }] },
      { indent: 0, parts: [{ t: 'AGENTS.md', c: '#e5a83a' }] },
      { indent: 0, parts: [{ t: 'Makefile', c: '#abb2bf' }] },
      { indent: 0, parts: [{ t: 'backend/', c: '#98c379' }] },
      { indent: 1, parts: [{ t: 'main.py', c: '#61afef' }] },
      { indent: 0, parts: [{ t: 'frontend/', c: '#98c379' }] },
      { indent: 1, parts: [{ t: 'src/', c: '#98c379' }] },
      { indent: 0, parts: [{ t: '.cahoots/', c: '#98c379' }] },
      { indent: 1, parts: [{ t: 'domain.md', c: '#61afef' }] },
      { indent: 1, parts: [{ t: 'stories/', c: '#98c379' }] },
    ],
  },
  'update-kit': {
    bg: 'linear-gradient(135deg, #0d1a2e 0%, #07101c 100%)',
    editorBg: '#0f1e30',
    lines: [
      { indent: 0, parts: [{ t: 'HOW-TO-USE.md', c: '#e5a83a' }] },
      { indent: 0, parts: [{ t: '.cahoots/', c: '#98c379' }] },
      { indent: 1, parts: [{ t: 'README.md', c: '#61afef' }] },
      { indent: 1, parts: [{ t: 'domain.md', c: '#61afef' }] },
      { indent: 1, parts: [{ t: 'stories/', c: '#98c379' }] },
      { indent: 2, parts: [{ t: 'auth.md', c: '#abb2bf' }] },
      { indent: 2, parts: [{ t: 'dashboard.md', c: '#abb2bf' }] },
      { indent: 0, parts: [{ t: '← drop into your project', c: '#4a5568' }] },
    ],
  },
};

function CodeThumbnail({ variant = 'default' }) {
  const config = CODE_VARIANTS[variant] || CODE_VARIANTS.default;
  return (
    <div style={{
      width: '100%', height: '100%',
      background: BG_LIGHT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        fontFamily: T.mono,
        fontSize: '8.5px',
        lineHeight: '1.65',
        color: '#abb2bf',
        width: '100%',
        maxWidth: '180px',
        backgroundColor: config.editorBg,
        borderRadius: '6px',
        padding: '12px 10px',
        boxShadow: '0 4px 12px rgba(46,33,9,0.14)',
      }}>
        {config.lines.map((line, i) => (
          <div key={i} style={{ paddingLeft: `${line.indent * 12}px` }}>
            {line.parts.map((p, j) => (
              <span key={j} style={{ color: p.c }}>{p.t}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Statement of Work — structured list with a scope bracket indicator
function StatementOfWorkThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 150 }}>
        <div style={{ height: 4, backgroundColor: '#0ea5e9' }} />
        <div style={{ padding: '12px 12px 14px' }}>
          <Line w={65} h={6} color="#1a1a1a" mb={4} />
          <Line w={40} h={3} color="#999" mb={10} />
          {[['IN SCOPE', '#16a34a'], ['OUT OF SCOPE', '#dc2626']].map(([label, color]) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 6, fontWeight: 700, color, marginBottom: 3, letterSpacing: '0.05em' }}>{label}</div>
              <Line w={90} mb={2} /><Line w={75} mb={0} />
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <Line w={50} h={4} color="#555" mb={4} />
            <Line w={100} mb={2} /><Line w={80} mb={0} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Test Plan — table with TC IDs and pass/fail cells
function TestPlanThumbnail() {
  const rows = [['TC-001', ''], ['TC-002', ''], ['TC-003', '']];
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 150 }}>
        <div style={{ height: 4, backgroundColor: '#f59e0b' }} />
        <div style={{ padding: '12px 12px 14px' }}>
          <Line w={60} h={6} color="#1a1a1a" mb={8} />
          <div style={{ display: 'grid', gridTemplateColumns: '30px 1fr 20px', gap: 2, marginBottom: 6 }}>
            <div style={{ height: 5, background: '#555', borderRadius: 2 }} />
            <div style={{ height: 5, background: '#555', borderRadius: 2 }} />
            <div style={{ height: 5, background: '#555', borderRadius: 2 }} />
          </div>
          {rows.map(([id], i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '30px 1fr 20px', gap: 2, marginBottom: 3 }}>
              <div style={{ height: 4, background: '#bbb', borderRadius: 2 }} />
              <div style={{ height: 4, background: '#ddd', borderRadius: 2 }} />
              <div style={{ height: 4, background: i < 2 ? '#16a34a66' : '#f3f4f6', borderRadius: 2 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// RTM — dense matrix grid
function RtmThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 150 }}>
        <div style={{ height: 4, backgroundColor: '#8b5cf6' }} />
        <div style={{ padding: '10px 10px 12px' }}>
          <Line w={55} h={6} color="#1a1a1a" mb={8} />
          {[5, 4, 5, 4, 5].map((cols, row) => (
            <div key={row} style={{ display: 'flex', gap: 2, marginBottom: 2 }}>
              {Array.from({ length: cols }).map((_, col) => (
                <div key={col} style={{
                  flex: 1, height: row === 0 ? 5 : 4,
                  background: row === 0 ? '#888' : col === 0 ? '#a78bfa55' : '#e5e7eb',
                  borderRadius: 1,
                }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// WBS — indented hierarchy
function WbsThumbnail() {
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 150 }}>
        <div style={{ height: 4, backgroundColor: '#10b981' }} />
        <div style={{ padding: '12px 12px 14px' }}>
          <Line w={55} h={6} color="#1a1a1a" mb={8} />
          {[
            { indent: 0, w: 80, h: 5, color: '#333' },
            { indent: 10, w: 65, h: 4, color: '#666' },
            { indent: 20, w: 50, h: 3, color: '#999' },
            { indent: 20, w: 45, h: 3, color: '#999' },
            { indent: 10, w: 60, h: 4, color: '#666' },
            { indent: 20, w: 48, h: 3, color: '#999' },
            { indent: 0, w: 75, h: 5, color: '#333' },
            { indent: 10, w: 55, h: 4, color: '#666' },
          ].map(({ indent, w, h, color }, i) => (
            <div key={i} style={{ paddingLeft: indent, marginBottom: 3 }}>
              <div style={{ height: h, background: color, borderRadius: 2, width: `${w}%` }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Risk Register — table with colored severity dots
function RiskRegisterThumbnail() {
  const risks = [
    { color: '#dc2626', w: 70 },
    { color: '#dc2626', w: 55 },
    { color: '#f59e0b', w: 65 },
    { color: '#f59e0b', w: 50 },
    { color: '#16a34a', w: 60 },
  ];
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...PAPER, width: '100%', maxWidth: 150 }}>
        <div style={{ height: 4, backgroundColor: '#ef4444' }} />
        <div style={{ padding: '12px 12px 14px' }}>
          <Line w={60} h={6} color="#1a1a1a" mb={8} />
          <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
            <div style={{ width: 8, height: 5, background: '#888', borderRadius: 1 }} />
            <div style={{ flex: 1, height: 5, background: '#888', borderRadius: 1 }} />
            <div style={{ width: 20, height: 5, background: '#888', borderRadius: 1 }} />
          </div>
          {risks.map(({ color, w }, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <div style={{ height: 4, background: '#ddd', borderRadius: 2, flex: 1 }} />
              <div style={{ width: 20, height: 4, background: `${color}44`, borderRadius: 2 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Story Map — column layout: features across top, stories below
function StoryMapThumbnail() {
  const cols = [
    { color: '#6366f1', stories: 3 },
    { color: '#0ea5e9', stories: 2 },
    { color: '#10b981', stories: 2 },
  ];
  return (
    <div style={{ width: '100%', height: '100%', background: BG_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 160 }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          {cols.map(({ color }, i) => (
            <div key={i} style={{ flex: 1, height: 12, background: color, borderRadius: 3, opacity: 0.85 }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {cols.map(({ color, stories }, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {Array.from({ length: stories }).map((_, j) => (
                <div key={j} style={{
                  height: 16, background: '#FFFBEC',
                  border: `1px solid ${color}66`,
                  borderRadius: 3,
                }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Thumbnail({ doc }) {
  switch (doc.thumbnail) {
    case 'product-brief':    return <ProductBriefThumbnail />;
    case 'one-pager':        return <OnePagerThumbnail />;
    case 'arch-overview':    return <ArchOverviewThumbnail />;
    case 'technical-spec':   return <TechnicalSpecThumbnail />;
    case 'sprint-brief':     return <SprintBriefThumbnail />;
    case 'feature-prd':      return <FeaturePRDThumbnail />;
    case 'readme':           return <ReadmeThumbnail />;
    case 'release-notes':    return <ReleaseNotesThumbnail />;
    case 'slides':           return <SlidesThumbnail />;
    case 'table':            return <TableThumbnail />;
    case 'json':             return <CodeThumbnail variant="default" />;
    case 'code':             return <CodeThumbnail variant={doc.codeVariant || 'default'} />;
    case 'statement-of-work': return <StatementOfWorkThumbnail />;
    case 'test-plan':        return <TestPlanThumbnail />;
    case 'rtm':              return <RtmThumbnail />;
    case 'wbs':              return <WbsThumbnail />;
    case 'risk-register':    return <RiskRegisterThumbnail />;
    case 'story-map':        return <StoryMapThumbnail />;
    default:                 return <ProductBriefThumbnail />;
  }
}

// ── Info popover ──────────────────────────────────────────────────────────────

function InfoPopover({ description }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: T.inkFaint, display: 'flex', alignItems: 'center', lineHeight: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = T.inkMuted; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = T.inkFaint; }}
      >
        <InformationCircleIcon style={{ width: 15, height: 15 }} />
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: T.card,
            border: `1px solid ${T.rule}`,
            borderRadius: T.r3,
            padding: '10px 12px',
            zIndex: 50, width: '240px',
            boxShadow: T.sh2,
            fontSize: '12px',
            color: T.inkMuted,
            lineHeight: '1.6',
            fontFamily: T.sans,
          }}>
            {description}
          </div>
        </>
      )}
    </div>
  );
}

// ── Preview modal ─────────────────────────────────────────────────────────────

function sanitizeMermaid(chart) {
  return chart
    // subgraph ID [label]  →  subgraph ID["label"]  (unquoted label)
    .replace(/^(\s*subgraph\s+\w+)\s+\[([^\]"]+)\]/gm, '$1["$2"]')
    // node[label with spaces/parens/etc]  →  node["label"]
    .replace(/\b(\w+)\[([^\]"]+)\]/g, (match, id, label) => {
      if (/[\s()/\\+*&%#@!?,.:;'`]/.test(label)) return `${id}["${label}"]`;
      return match;
    });
}

function MermaidDiagram({ chart }) {
  const ref = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const sanitized = sanitizeMermaid(chart);
    import('mermaid')
      .then(({ default: mermaid }) => {
        mermaid.initialize({ startOnLoad: false, theme: 'dark', securityLevel: 'loose' });
        const id = `mermaid-${Math.random().toString(36).slice(2)}`;
        return mermaid.render(id, sanitized);
      })
      .then(({ svg: rendered }) => { if (!cancelled) setSvg(rendered); })
      .catch((e) => { if (!cancelled) setError(String(e)); });
    return () => { cancelled = true; };
  }, [chart]);

  if (error) return (
    <div style={{ backgroundColor: T.paperDeep, border: `1px solid ${T.capRed}`, borderRadius: T.r3, padding: '16px', margin: '0 0 20px' }}>
      <div style={{ color: T.capRed, fontSize: '12px', marginBottom: '8px', fontFamily: T.sans }}>Diagram error: {error}</div>
      <pre style={{ color: T.ink2, fontSize: '11px', whiteSpace: 'pre-wrap', margin: 0, fontFamily: T.mono }}>{chart}</pre>
    </div>
  );
  if (!svg) return (
    <div style={{ padding: '24px', textAlign: 'center', color: T.inkMuted, fontSize: '13px', marginBottom: '20px', fontFamily: T.sans }}>
      Rendering diagram…
    </div>
  );
  return (
    <div
      ref={ref}
      style={{ margin: '0 0 20px', overflow: 'auto', backgroundColor: T.paperDeep, borderRadius: T.r3, padding: '16px' }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

const MD_COMPONENTS = {
  h1: ({ children }) => (
    <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: T.ink, margin: '0 0 12px', lineHeight: 1.2, fontFamily: T.sans }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: T.ink, margin: '36px 0 10px', lineHeight: 1.3, fontFamily: T.sans }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: T.ink, margin: '28px 0 8px', lineHeight: 1.3, fontFamily: T.sans }}>{children}</h3>
  ),
  p: ({ children }) => (
    <p style={{ margin: '0 0 16px', color: T.inkMuted, lineHeight: '1.75', fontFamily: T.sans }}>{children}</p>
  ),
  ul: ({ children }) => (
    <ul style={{ margin: '0 0 20px', paddingLeft: '20px', listStyleType: 'disc' }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: '0 0 20px', paddingLeft: '20px' }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li style={{ margin: '0 0 8px', color: T.inkMuted, lineHeight: '1.65', fontFamily: T.sans }}>{children}</li>
  ),
  strong: ({ children }) => (
    <strong style={{ color: T.ink2, fontWeight: 600 }}>{children}</strong>
  ),
  hr: () => (
    <hr style={{ border: 'none', borderTop: `1px solid ${T.rule}`, margin: '32px 0' }} />
  ),
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: `3px solid ${T.ruleStrong}`, margin: '0 0 16px', paddingLeft: '16px', color: T.inkMuted, fontStyle: 'italic' }}>{children}</blockquote>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th style={{ padding: '8px 12px', borderBottom: `1px solid ${T.ruleStrong}`, textAlign: 'left', color: T.ink2, fontWeight: 600, backgroundColor: T.paperDeep, fontFamily: T.sans }}>{children}</th>
  ),
  td: ({ children }) => (
    <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.rule}`, color: T.inkMuted, fontFamily: T.sans }}>{children}</td>
  ),
  // In react-markdown v9, block code flows through `pre`, not `code`.
  // `code` only handles inline code.
  code: ({ children }) => (
    <code style={{ fontFamily: T.mono, fontSize: '0.875em', backgroundColor: T.paperDeep, padding: '1px 5px', borderRadius: T.r2, color: T.orange }}>{children}</code>
  ),
  pre: ({ children }) => {
    // children is the <code> element rendered by react-markdown
    const codeEl = Array.isArray(children) ? children[0] : children;
    const className = codeEl?.props?.className || '';
    const raw = String(codeEl?.props?.children ?? '').trim();
    const isMermaid =
      className.includes('language-mermaid') ||
      raw.startsWith('flowchart ') ||
      raw.startsWith('graph ') ||
      raw.startsWith('sequenceDiagram') ||
      raw.startsWith('classDiagram') ||
      raw.startsWith('erDiagram');
    if (isMermaid) return <MermaidDiagram chart={raw} />;
    return (
      <pre style={{ backgroundColor: T.paperDeep, border: `1px solid ${T.rule}`, borderRadius: T.r3, padding: '16px', overflow: 'auto', margin: '0 0 20px' }}>
        <code style={{ fontFamily: T.mono, fontSize: '12px', color: T.ink2, lineHeight: '1.6' }}>{codeEl?.props?.children}</code>
      </pre>
    );
  },
};

function PreviewModal({ doc, content, filename, onDownload, onClose }) {
  const isCode = doc.id === 'json' || doc.id === 'backlog_csv';
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      backgroundColor: 'rgba(46,33,9,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        backgroundColor: T.card,
        border: `1px solid ${T.rule}`,
        borderRadius: T.r4,
        width: '100%', maxWidth: '820px', maxHeight: '88vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(46,33,9,0.22)',
        fontFamily: T.sans,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: `1px solid ${T.rule}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px', fontWeight: 600, color: T.ink, fontFamily: T.sans }}>
              {doc.name}
            </span>
            <span style={{
              fontSize: '11px', fontFamily: T.mono,
              color: T.inkMuted,
              backgroundColor: T.paperDeep,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2, padding: '1px 7px',
            }}>
              {filename || doc.format}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.inkFaint, display: 'flex', alignItems: 'center', padding: '4px' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.inkMuted; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.inkFaint; }}
          >
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1, overflow: 'auto',
          padding: '24px',
          backgroundColor: isCode ? T.paperDeep : T.paperSoft,
        }}>
          {isCode ? (
            <pre style={{
              margin: 0, fontFamily: T.mono,
              fontSize: '12px', color: T.ink2, lineHeight: '1.65',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {content}
            </pre>
          ) : (
            <div style={{ color: T.ink, lineHeight: '1.75', fontSize: '13px', maxWidth: '680px' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: '12px',
          padding: '14px 20px',
          borderTop: `1px solid ${T.rule}`,
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 16px',
              background: 'none', border: `1px solid ${T.rule}`,
              borderRadius: T.r3, color: T.inkMuted,
              cursor: 'pointer', fontFamily: T.sans, fontSize: '13px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.ink2; e.currentTarget.style.borderColor = T.ruleStrong; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.inkMuted; e.currentTarget.style.borderColor = T.rule; }}
          >
            Close
          </button>
          <button
            onClick={onDownload}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '7px 16px',
              backgroundColor: T.orange, border: 'none',
              borderRadius: T.r3, color: 'white',
              cursor: 'pointer', fontFamily: T.sans,
              fontSize: '13px', fontWeight: 600,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = T.orangeDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = T.orange; }}
          >
            <ArrowDownTrayIcon style={{ width: 15, height: 15 }} />
            Save file
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Feature picker modal ──────────────────────────────────────────────────────

// Generic picker modal — used for both feature and plan selection
// getItemDisabled(name) → string reason if disabled, null/undefined if enabled
function PickerModal({ title, subtitle, emptyMessage, items, onSelect, onClose, confirmLabel = 'Generate Preview →', getItemDisabled }) {
  const [selected, setSelected] = useState(null);
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      backgroundColor: 'rgba(46,33,9,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        backgroundColor: T.card,
        border: `1px solid ${T.rule}`,
        borderRadius: T.r4,
        width: '100%', maxWidth: '420px',
        boxShadow: '0 16px 48px rgba(46,33,9,0.18)',
        overflow: 'hidden',
        fontFamily: T.sans,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: `1px solid ${T.rule}`,
        }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: T.ink }}>
              {title}
            </div>
            <div style={{ fontSize: '12px', color: T.inkMuted, marginTop: '2px' }}>
              {subtitle}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.inkFaint, display: 'flex', padding: '4px' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.inkMuted; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.inkFaint; }}
          >
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Item list */}
        <div style={{ maxHeight: '320px', overflowY: 'auto', padding: '6px 0' }}>
          {items.length === 0 ? (
            <div style={{ padding: '16px 20px', color: T.inkMuted, fontSize: '13px' }}>
              {emptyMessage}
            </div>
          ) : items.map((item) => {
            const name = typeof item === 'string' ? item : (item.name || '');
            const desc = typeof item === 'object' ? (item.description || '') : '';
            const disabledReason = getItemDisabled ? getItemDisabled(name) : null;
            const isDisabled = Boolean(disabledReason);
            const isSelected = !isDisabled && selected === name;

            const row = (
              <button
                key={name}
                onClick={() => !isDisabled && setSelected(name)}
                disabled={isDisabled}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 20px',
                  background: isSelected ? `${T.orange}12` : 'none',
                  border: 'none',
                  borderLeft: isSelected ? `2px solid ${T.orange}` : '2px solid transparent',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  fontFamily: T.sans,
                  opacity: isDisabled ? 0.4 : 1,
                  transition: 'all 100ms ease',
                }}
                onMouseEnter={(e) => { if (!isSelected && !isDisabled) e.currentTarget.style.backgroundColor = T.paper; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <div style={{ fontSize: '13px', fontWeight: isSelected ? 600 : 500, color: isSelected ? T.orange : T.ink }}>
                  {name}
                </div>
                {desc && (
                  <div style={{ fontSize: '12px', color: T.inkMuted, marginTop: '2px', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {desc}
                  </div>
                )}
              </button>
            );

            return isDisabled ? (
              <Tooltip key={name} content={disabledReason} position="right">
                <div style={{ display: 'block' }}>{row}</div>
              </Tooltip>
            ) : row;
          })}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: '12px',
          padding: '14px 20px',
          borderTop: `1px solid ${T.rule}`,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 16px',
              background: 'none', border: `1px solid ${T.rule}`,
              borderRadius: T.r3, color: T.inkMuted,
              cursor: 'pointer', fontFamily: T.sans, fontSize: '13px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.ink2; e.currentTarget.style.borderColor = T.ruleStrong; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.inkMuted; e.currentTarget.style.borderColor = T.rule; }}
          >
            Cancel
          </button>
          <button
            onClick={() => selected && onSelect(selected)}
            disabled={!selected}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '7px 16px',
              backgroundColor: selected ? T.orange : T.paperDeep,
              border: `1px solid ${selected ? T.orange : T.rule}`,
              borderRadius: T.r3,
              color: selected ? 'white' : T.inkFaint,
              cursor: selected ? 'pointer' : 'default',
              fontFamily: T.sans, fontSize: '13px',
              fontWeight: 600,
              transition: 'all 150ms ease',
            }}
            onMouseEnter={(e) => { if (selected) e.currentTarget.style.backgroundColor = T.orangeDark; }}
            onMouseLeave={(e) => { if (selected) e.currentTarget.style.backgroundColor = T.orange; }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Export card ───────────────────────────────────────────────────────────────

function ExportCard({ doc, ideaId, ideaName, ideaSpec, onShowUpgrade }) {
  const { canExport, canUseUpdateKit } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [preview, setPreview] = useState(null);
  const exportMutation = useExportIdea();

  const posthog = usePostHog();
  const isPlanPicker = doc.interactive && doc.interactiveType === 'plan';
  const isFeaturePicker = doc.interactive && !doc.interactiveType;

  // Fetch plans for both plan-picker and feature-picker cards
  const { data: plansData } = useIdeaPlans(doc.interactive ? ideaId : null);
  const plans = (plansData?.items || []).map(p => ({ name: p.name || p.description || 'Plan', description: p.description || '' }));
  const planIdByName = Object.fromEntries(
    (plansData?.items || []).map(p => [p.name || p.description || 'Plan', p.id])
  );

  // For feature-picker docs: derive which features are in at least one completed plan
  const featuresInCompletedPlans = useMemo(() => {
    if (!isFeaturePicker) return null;
    const allPlans = plansData?.items || [];
    const completedPlans = allPlans.filter(p => p.status === 'completed');
    const names = new Set();
    completedPlans.forEach(p => (p.selected_features || []).forEach(n => names.add(n)));
    return names;
  }, [plansData, isFeaturePicker]);

  const getFeatureDisabledReason = isFeaturePicker
    ? (name) => {
        if (!featuresInCompletedPlans) return null;
        const allPlans = plansData?.items || [];
        const plansWithFeature = allPlans.filter(p => (p.selected_features || []).includes(name));
        if (plansWithFeature.length === 0) return 'Add this feature to a plan first';
        const hasCompleted = plansWithFeature.some(p => p.status === 'completed');
        if (!hasCompleted) return 'Plan is still generating';
        return null;
      }
    : null;

  const downloadZip = async (planId, format = 'cahoots') => {
    const hasAccess = format === 'update_kit' ? canUseUpdateKit() : canExport();
    if (!hasAccess) {
      posthog?.capture('feature_gate_hit', { feature: format, format: doc.id, category: doc.category });
      onShowUpgrade();
      return;
    }
    setPicking(false);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = window.CAHOOTS_CONFIG?.API_URL || '/api';
      const response = await fetch(`${apiUrl}/plans/${planId}/agent-export?format=${format}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition');
      let filename = `${format}.zip`;
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      posthog?.capture('export_downloaded', { format: doc.id, category: 'AI Tools' });
    } catch (err) {
      console.error('ZIP download failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const generate = async ({ featureName = null, planName = null } = {}) => {
    if (!canExport()) {
      posthog?.capture('feature_gate_hit', { feature: 'export', format: doc.id, category: doc.category });
      onShowUpgrade();
      return;
    }
    setPicking(false);
    setLoading(true);
    try {
      const result = await exportMutation.mutateAsync({
        ideaId,
        format: doc.id,
        includePlans: true,
        includeSpec: true,
        includeAssumptions: true,
        ...(featureName ? { featureName } : {}),
        ...(planName ? { planName } : {}),
      });
      const content = doc.id === 'json'
        ? JSON.stringify(result.content, null, 2)
        : result.content;
      setPreview({ content, filename: result.filename });
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    if (doc.interactive) {
      const hasAccess = doc.id === 'ai_update_kit' ? canUseUpdateKit() : canExport();
      if (!hasAccess) {
        posthog?.capture('feature_gate_hit', { feature: doc.id === 'ai_update_kit' ? 'update_kit' : 'export', format: doc.id, category: doc.category });
        onShowUpgrade();
        return;
      }
      setPicking(true);
    } else {
      generate();
    }
  };

  const handleDownload = () => {
    if (!preview) return;
    posthog?.capture('export_downloaded', { format: doc.id, category: doc.category });
    const blob = new Blob([preview.content], { type: getMimeType(doc.id) });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = preview.filename || `${ideaName || 'idea'}-export`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const features = ideaSpec?.core_features || [];
  const buttonLabel = loading
    ? 'Generating…'
    : doc.interactive
      ? (isPlanPicker ? 'Choose Plan →' : 'Choose Feature →')
      : 'Preview & Download';

  return (
    <>
      <div
        style={{
          borderRadius: T.r4,
          border: `1px solid ${T.rule}`,
          backgroundColor: T.card,
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: T.sh1,
          transition: 'border-color 150ms ease, box-shadow 150ms ease',
          fontFamily: T.sans,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = T.ruleStrong;
          e.currentTarget.style.boxShadow = T.sh2;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = T.rule;
          e.currentTarget.style.boxShadow = T.sh1;
        }}
      >
        <div style={{ height: '180px', flexShrink: 0, overflow: 'hidden' }}>
          <Thumbnail doc={doc} />
        </div>
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: T.ink, lineHeight: 1.3, fontFamily: T.sans }}>
                {doc.name}
              </span>
              <InfoPopover description={doc.description} />
            </div>
            <span style={{
              fontSize: '11px', fontFamily: T.mono,
              color: T.inkMuted,
              backgroundColor: T.paperDeep,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2, padding: '1px 7px',
              flexShrink: 0,
            }}>
              {doc.format}
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={handleButtonClick}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '6px', width: '100%',
              padding: '8px 12px',
              backgroundColor: T.orange,
              border: 'none',
              borderRadius: T.r3,
              color: 'white',
              cursor: loading ? 'default' : 'pointer',
              fontFamily: T.sans, fontSize: '13px',
              fontWeight: 600,
              opacity: loading ? 0.65 : 1, transition: 'all 150ms ease',
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = T.orangeDark;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = T.orange;
            }}
          >
            {!doc.interactive && <ArrowDownTrayIcon style={{ width: 14, height: 14, flexShrink: 0 }} />}
            {buttonLabel}
          </button>
        </div>
      </div>

      {picking && isPlanPicker && (
        <PickerModal
          title="Choose a plan"
          subtitle={
            doc.id === 'ai_update_kit'
              ? 'Download the AI Update Kit for one of your plans'
              : doc.isZip
                ? 'Download the AI Starter Kit for one of your plans'
                : 'Generate a Sprint Brief for one of your plans'
          }
          emptyMessage="No plans found. Create a plan from this idea first."
          confirmLabel={doc.isZip ? 'Download ZIP →' : 'Generate Preview →'}
          items={plans}
          onSelect={(planName) => {
            if (doc.isZip) {
              const planId = planIdByName[planName];
              if (planId) {
                const zipFormat = doc.id === 'ai_update_kit' ? 'update_kit' : 'cahoots';
                downloadZip(planId, zipFormat);
              }
            } else {
              generate({ planName });
            }
          }}
          onClose={() => setPicking(false)}
        />
      )}

      {picking && !isPlanPicker && (
        <PickerModal
          title="Choose a feature"
          subtitle="Generate a document scoped to one feature from your spec"
          emptyMessage="No features found in this idea's spec yet."
          items={features}
          getItemDisabled={getFeatureDisabledReason}
          onSelect={(featureName) => generate({ featureName })}
          onClose={() => setPicking(false)}
        />
      )}

      {preview && (
        <PreviewModal
          doc={doc}
          content={preview.content}
          filename={preview.filename}
          onDownload={handleDownload}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}

// ── Hero export card (.cahoots/) ──────────────────────────────────────────────

function HeroExportCard({ ideaId, ideaName, onShowUpgrade }) {
  const { canExport } = useSubscription();
  const posthog = usePostHog();
  const [loading, setLoading] = useState(null); // 'claude' | 'codex' | null
  const [picking, setPicking] = useState(null);  // 'claude' | 'codex' | null

  const { data: plansData } = useIdeaPlans(ideaId);
  const plans = (plansData?.items || []).map(p => ({ name: p.name || p.description || 'Plan', description: p.description || '' }));
  const planIdByName = Object.fromEntries(
    (plansData?.items || []).map(p => [p.name || p.description || 'Plan', p.id])
  );

  const handleClick = (agent) => {
    if (!canExport()) {
      posthog?.capture('feature_gate_hit', { feature: 'export', format: 'ai_starter_kit', category: 'AI Tools' });
      onShowUpgrade();
      return;
    }
    setPicking(agent);
  };

  const downloadZip = async (planId, agent) => {
    setPicking(null);
    setLoading(agent);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = window.CAHOOTS_CONFIG?.API_URL || '/api';
      const response = await fetch(`${apiUrl}/plans/${planId}/agent-export?format=cahoots&agent=${agent}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition');
      let filename = `cahoots-${agent}.zip`;
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      posthog?.capture('export_downloaded', { format: 'ai_starter_kit', agent, category: 'AI Tools' });
    } catch (err) {
      console.error('ZIP download failed:', err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <div style={{
        borderRadius: T.r4,
        border: `1px solid ${T.rule}`,
        backgroundColor: T.card,
        boxShadow: T.sh1,
        overflow: 'hidden',
        display: 'flex',
        flexWrap: 'wrap',
        fontFamily: T.sans,
      }}>
        {/* Left: thumbnail */}
        <div style={{ width: '260px', flexShrink: 0, borderRight: `1px solid ${T.rule}` }}>
          <CodeThumbnail variant="zip" />
        </div>

        {/* Right: content */}
        <div style={{ flex: 1, padding: '24px', minWidth: '260px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '12px' }}>
          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '18px',
              fontWeight: 700,
              color: T.ink,
              fontFamily: T.sans,
            }}>
              AI Starter Bundle
            </span>
            <span style={{
              fontSize: '11px', fontFamily: T.mono,
              color: T.inkMuted,
              backgroundColor: T.paperDeep,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2, padding: '1px 7px',
            }}>
              .zip
            </span>
            <span style={{
              fontSize: '11px',
              fontWeight: 700,
              color: T.moss,
              backgroundColor: `${T.moss}14`,
              border: `1px solid ${T.moss}44`,
              borderRadius: T.r2,
              padding: '1px 7px',
              letterSpacing: '0.03em',
            }}>Hobbyist</span>
          </div>

          <p style={{
            fontSize: '13px',
            color: T.inkMuted,
            margin: 0,
            lineHeight: '1.6',
            maxWidth: '560px',
            fontFamily: T.sans,
          }}>
            A complete project starter — <code style={{ fontFamily: T.mono, fontSize: '11px', backgroundColor: T.paperDeep, padding: '1px 4px', borderRadius: T.r2, color: T.ink2 }}>Makefile</code>, backend and frontend stubs, plus a <code style={{ fontFamily: T.mono, fontSize: '11px', backgroundColor: T.paperDeep, padding: '1px 4px', borderRadius: T.r2, color: T.ink2 }}>.cahoots/</code> implementation guide with your domain model and feature stories.
            Drop it in a new folder and tell your agent to build it.
          </p>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { agent: 'claude', label: 'Claude Code', file: 'CLAUDE.md' },
              { agent: 'codex',  label: 'Codex',       file: 'AGENTS.md' },
            ].map(({ agent, label, file }) => (
              <button
                key={agent}
                onClick={() => handleClick(agent)}
                disabled={!!loading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: T.orange,
                  border: 'none',
                  borderRadius: T.r3,
                  color: 'white',
                  cursor: loading ? 'default' : 'pointer',
                  fontFamily: T.sans,
                  fontSize: '13px',
                  fontWeight: 600,
                  opacity: loading ? 0.65 : 1,
                  transition: 'all 150ms ease',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = T.orangeDark;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = T.orange;
                }}
              >
                <ArrowDownTrayIcon style={{ width: 14, height: 14 }} />
                {loading === agent ? 'Downloading…' : `${label} Bundle`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {picking && (
        <PickerModal
          title="Choose a plan"
          subtitle={`Download the ${picking === 'claude' ? 'Claude Code' : 'Codex'} Bundle for one of your plans`}
          emptyMessage="No plans found. Create a plan from this idea first."
          confirmLabel={`Download ${picking === 'claude' ? 'Claude Code' : 'Codex'} Bundle →`}
          items={plans}
          onSelect={(planName) => {
            const planId = planIdByName[planName];
            if (planId) downloadZip(planId, picking);
          }}
          onClose={() => setPicking(null)}
        />
      )}
    </>
  );
}

// ── Hero update kit card (.cahoots/ drop-in) ─────────────────────────────────

function HeroUpdateKitCard({ ideaId, ideaName, onShowUpgrade }) {
  const { canUseUpdateKit } = useSubscription();
  const posthog = usePostHog();
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);

  const { data: plansData } = useIdeaPlans(ideaId);
  const plans = (plansData?.items || []).map(p => ({ name: p.name || p.description || 'Plan', description: p.description || '' }));
  const planIdByName = Object.fromEntries(
    (plansData?.items || []).map(p => [p.name || p.description || 'Plan', p.id])
  );

  const handleClick = () => {
    if (!canUseUpdateKit()) {
      posthog?.capture('feature_gate_hit', { feature: 'update_kit', format: 'ai_update_kit', category: 'AI Tools' });
      onShowUpgrade();
      return;
    }
    setPicking(true);
  };

  const downloadZip = async (planId) => {
    setPicking(false);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = window.CAHOOTS_CONFIG?.API_URL || '/api';
      const response = await fetch(`${apiUrl}/plans/${planId}/agent-export?format=update_kit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition');
      let filename = 'update-kit.zip';
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      posthog?.capture('export_downloaded', { format: 'ai_update_kit', category: 'AI Tools' });
    } catch (err) {
      console.error('ZIP download failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{
        borderRadius: T.r4,
        border: `1px solid ${T.rule}`,
        backgroundColor: T.card,
        boxShadow: T.sh1,
        overflow: 'hidden',
        display: 'flex',
        flexWrap: 'wrap',
        fontFamily: T.sans,
      }}>
        {/* Left: thumbnail */}
        <div style={{ width: '260px', flexShrink: 0, borderRight: `1px solid ${T.rule}` }}>
          <CodeThumbnail variant="update-kit" />
        </div>

        {/* Right: content */}
        <div style={{ flex: 1, padding: '24px', minWidth: '260px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '12px' }}>
          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '18px',
              fontWeight: 700,
              color: T.ink,
              fontFamily: T.sans,
            }}>
              AI Update Kit
            </span>
            <span style={{
              fontSize: '11px', fontFamily: T.mono,
              color: T.inkMuted,
              backgroundColor: T.paperDeep,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2, padding: '1px 7px',
            }}>
              .zip
            </span>
            <span style={{
              fontSize: '11px',
              fontWeight: 700,
              color: T.moss,
              backgroundColor: `${T.moss}14`,
              border: `1px solid ${T.moss}44`,
              borderRadius: T.r2,
              padding: '1px 7px',
              letterSpacing: '0.03em',
            }}>Hobbyist</span>
          </div>

          <p style={{
            fontSize: '13px',
            color: T.inkMuted,
            margin: 0,
            lineHeight: '1.6',
            maxWidth: '560px',
            fontFamily: T.sans,
          }}>
            A drop-in <code style={{ fontFamily: T.mono, fontSize: '11px', backgroundColor: T.paperDeep, padding: '1px 4px', borderRadius: T.r2, color: T.ink2 }}>.cahoots/</code> folder for your existing project. Contains your domain model and feature stories — no scaffold, no boilerplate. Drop it in and tell your agent to build what's missing.
          </p>

          <div>
            <button
              onClick={handleClick}
              disabled={loading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px',
                backgroundColor: T.orange,
                border: 'none',
                borderRadius: T.r3,
                color: 'white',
                cursor: loading ? 'default' : 'pointer',
                fontFamily: T.sans,
                fontSize: '13px',
                fontWeight: 600,
                opacity: loading ? 0.65 : 1,
                transition: 'all 150ms ease',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = T.orangeDark;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = T.orange;
              }}
            >
              <ArrowDownTrayIcon style={{ width: 14, height: 14 }} />
              {loading ? 'Downloading…' : 'Choose Plan →'}
            </button>
          </div>
        </div>
      </div>

      {picking && (
        <PickerModal
          title="Choose a plan"
          subtitle="Download the AI Update Kit for one of your plans"
          emptyMessage="No plans found. Create a plan from this idea first."
          confirmLabel="Download ZIP →"
          items={plans}
          onSelect={(planName) => {
            const planId = planIdByName[planName];
            if (planId) downloadZip(planId);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}

// ── Category section ──────────────────────────────────────────────────────────

const CATEGORIES = ['Stakeholder', 'Engineering', 'AI Tools', 'Data', 'Publishing'];

// ── Main export ───────────────────────────────────────────────────────────────

const DownloadCatalog = ({ ideaId, idea, onShowUpgrade }) => {
  const hasGitHub = (idea?.github_repos?.length ?? 0) > 0;

  return (
    <div style={{ fontFamily: T.sans }}>
      <div style={{ marginBottom: '28px' }}>
        <div style={{ fontFamily: T.hand, fontSize: 22, color: T.orange, lineHeight: 1 }}>take it with you —</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: T.ink, margin: '4px 0 6px', letterSpacing: -0.6, fontFamily: T.sans }}>
          Download
        </h1>
        <p style={{ fontSize: '14px', color: T.ink2, margin: 0, fontFamily: T.sans }}>
          Export your plan in the shape that fits your workflow.
        </p>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <div style={{
          fontSize: '11px',
          fontWeight: 700,
          color: T.inkFaint,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: '16px',
          paddingBottom: '8px',
          borderBottom: `1px solid ${T.rule}`,
          fontFamily: T.sans,
        }}>
          Recommended
        </div>
        {hasGitHub
          ? <HeroUpdateKitCard ideaId={ideaId} ideaName={idea?.name} onShowUpgrade={onShowUpgrade} />
          : <HeroExportCard ideaId={ideaId} ideaName={idea?.name} onShowUpgrade={onShowUpgrade} />
        }
      </div>

      {CATEGORIES.map((category) => {
        const excludeId = hasGitHub ? 'ai_update_kit' : 'ai_starter_kit';
        const docs = CATALOG.filter(d => d.category === category && d.id !== excludeId);
        if (!docs.length) return null;
        return (
          <div key={category} style={{ marginBottom: '40px' }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 700,
              color: T.inkFaint,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: `1px solid ${T.rule}`,
              fontFamily: T.sans,
            }}>
              {category}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 260px))',
              gap: '16px',
            }}>
              {docs.map((doc) => (
                <ExportCard
                  key={doc.id}
                  doc={doc}
                  ideaId={ideaId}
                  ideaName={idea?.name}
                  ideaSpec={idea?.idea_spec}
                  onShowUpgrade={onShowUpgrade}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DownloadCatalog;
